import React, { useEffect } from 'react';
import { ImageBackground, Modal, StyleSheet, Text, View } from 'react-native';

import LeveledUp from '../../../assets/popup/modal_level_up.png';
import { cleverTapService } from '../../../services/clevertap/clevertap.service';
import { analyticsEvents } from '../../../services/firebase/analytics-events.service';
import { SUPPORTED_MODAL_ORIENTATIONS } from '../../../store/types';
import { useDevice } from '../../../utils/hooks/useDevice';
import { useSafeViewport } from '../../../utils/hooks/useSafeViewport';
import { getSafeAreaSidePadding } from '../../../utils/safeArea';
import { playSound } from '../../../utils/sound';
import { TouchableOpacity } from '../common/Touchable';

interface LeveledUpNotificationModalProps {
	visible: boolean;
	level: number;
	onClose: () => void;
}

const LevelNumber = ({ level }: { level: number }) => {
	return (
		<View style={styles.levelContainer}>
			<Text style={[styles.levelStroke, styles.strokeN3_0]}>{level}</Text>
			<Text style={[styles.levelStroke, styles.stroke3_0]}>{level}</Text>
			<Text style={[styles.levelStroke, styles.stroke0_N3]}>{level}</Text>
			<Text style={[styles.levelStroke, styles.stroke0_3]}>{level}</Text>
			<Text style={[styles.levelStroke, styles.strokeN3_N3]}>{level}</Text>
			<Text style={[styles.levelStroke, styles.stroke3_N3]}>{level}</Text>
			<Text style={[styles.levelStroke, styles.strokeN3_3]}>{level}</Text>
			<Text style={[styles.levelStroke, styles.stroke3_3]}>{level}</Text>
			<Text style={styles.levelMain}>{level}</Text>
		</View>
	);
};

const LeveledUpNotificationModal = ({
	visible,
	level,
	onClose,
}: LeveledUpNotificationModalProps) => {
	const { isDesktop } = useDevice();
	const { width: screenWidth, insets } = useSafeViewport();
	const modalWidth = isDesktop ? Math.min(screenWidth * 0.42, 600) : screenWidth * 0.5;
	const safeOverlayInsets = getSafeAreaSidePadding(insets);

	const modalHeight = modalWidth * 0.7269;

	useEffect(() => {
		if (visible) {
			playSound('LevelUp'); // v1 modal.level.up.component
			analyticsEvents.trackLevelUp(level);
			cleverTapService.trackEvent('Level Up', {
				new_level: level,
			});
		}
	}, [visible, level]);

	useEffect(() => {
		if (visible) {
			const timer = setTimeout(() => onClose(), 3000);
			return () => clearTimeout(timer);
		}
	}, [visible, onClose]);

	return (
		<Modal
			visible={visible}
			transparent
			animationType='fade'
			presentationStyle='overFullScreen'
			statusBarTranslucent
			navigationBarTranslucent
			supportedOrientations={SUPPORTED_MODAL_ORIENTATIONS}
			onRequestClose={onClose}
		>
			<View style={[styles.overlay, safeOverlayInsets]}>
				<TouchableOpacity activeOpacity={1} onPress={onClose} style={styles.backdropTapArea} />
				<ImageBackground
					source={LeveledUp}
					style={[styles.bg, { width: modalWidth, height: modalHeight }]}
					resizeMode='contain'
				>
					<LevelNumber level={level} />
				</ImageBackground>
			</View>
		</Modal>
	);
};

const styles = StyleSheet.create({
	overlay: {
		flex: 1,
		backgroundColor: 'rgba(0,0,0,0.6)',
		justifyContent: 'center',
		alignItems: 'center',
	},
	backdropTapArea: {
		...StyleSheet.absoluteFill,
	},

	bg: {
		justifyContent: 'center',
		alignItems: 'center',
	},

	levelContainer: {
		position: 'absolute',
		top: '50%',
		alignItems: 'center',
		justifyContent: 'center',
		transform: [{ translateY: -65 }],
	},

	levelMain: {
		color: '#FFD84A',
		fontSize: 130,
		fontWeight: '900',
		textShadowColor: 'rgba(200, 0, 0, 0.7)',
		textShadowOffset: { width: 2, height: 2 },
		textShadowRadius: 4,
	},

	levelStroke: {
		position: 'absolute',
		color: 'white',
		fontSize: 130,
		fontWeight: '900',
	},
	strokeN3_0: { left: -3, top: 0 },
	stroke3_0: { left: 3, top: 0 },
	stroke0_N3: { left: 0, top: -3 },
	stroke0_3: { left: 0, top: 3 },
	strokeN3_N3: { left: -3, top: -3 },
	stroke3_N3: { left: 3, top: -3 },
	strokeN3_3: { left: -3, top: 3 },
	stroke3_3: { left: 3, top: 3 },
});

export default LeveledUpNotificationModal;
